import { useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  useFogbugzCase,
  useUpdateCase,
  useAddComment,
  useAttachFile,
  useResolveCase,
  useReopenCase,
  useSummarizeEvtxAttachment,
  type Attachment,
  type CaseEvent,
  type EvtxSummaryResponse,
} from "@/hooks/queries/use-fogbugz";
import { usePageTitle } from "@/hooks/use-page-title";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CommandCopyBox } from "@/components/common/command-copy-box";
import { buildBugToErBridgeCommand, getAgentPromptExplanation } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { HELP_IDS } from "@/lib/help-ids";
import { sanitizeFogBugzHtml } from "@/lib/fogbugz-html";
import { AttachmentsCard } from "@/components/fogbugz/attachments-card";
import { EvtxSummaryDialog } from "@/components/fogbugz/evtx-summary-dialog";
import { AuthenticatedHtmlContent } from "@/components/fogbugz/authenticated-html-content";
import { Lightbox } from "@/components/ui/lightbox";
import {
  ArrowLeft,
  Edit2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Upload,
  ExternalLink,
  RotateCcw,
} from "lucide-react";

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const parsedCaseId = id ? parseInt(id, 10) : NaN;
  const caseId = Number.isFinite(parsedCaseId) ? parsedCaseId : null;

  const { data, isLoading, error } = useFogbugzCase(caseId);
  const updateCase = useUpdateCase();
  const addComment = useAddComment();
  const attachFile = useAttachFile();
  const resolveCase = useResolveCase();
  const reopenCase = useReopenCase();
  const summarizeEvtx = useSummarizeEvtxAttachment();
  const { toast } = useToast();
  const host = useResolvedHost();

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [commentText, setCommentText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [evtxSummary, setEvtxSummary] = useState<EvtxSummaryResponse["data"] | null>(null);

  const openImage = useCallback(
    (src: string, alt: string) => setLightbox({ src, alt }),
    [],
  );

  usePageTitle(data?.data.case.sTitle ?? "Ticket");

  const caseData = data?.data.case;
  const linkedItem = data?.data.linkedSpecTreeItem;
  const fogbugzBaseUrl = data?.data.fogbugzBaseUrl;

  const handleEdit = () => {
    if (caseData) {
      setEditedTitle(caseData.sTitle);
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (caseId === null || !caseData) return;
    const trimmed = editedTitle.trim();
    if (!trimmed) return;

    await updateCase.mutateAsync({
      id: caseId,
      title: trimmed,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedTitle("");
  };

  const handleAddComment = async () => {
    if (caseId === null || !commentText.trim()) return;

    await addComment.mutateAsync({ id: caseId, text: commentText });
    setCommentText("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleAttachFile = async () => {
    if (caseId === null || !selectedFile) return;

    await attachFile.mutateAsync({ id: caseId, file: selectedFile });
    setSelectedFile(null);
  };

  const handleResolve = async () => {
    if (caseId === null) return;
    await resolveCase.mutateAsync(caseId);
  };

  const handleReopen = async () => {
    if (caseId === null) return;
    await reopenCase.mutateAsync(caseId);
  };

  // Opt-in .evtx summary (PI-SPEC-SW-1229). One at a time: the request can
  // take up to ~2 minutes, spends a model call, and posts to FogBugz, so a
  // second click while one is in flight is ignored rather than queued.
  const handleSummarizeEvtx = (attachment: Attachment) => {
    if (caseId === null || summarizeEvtx.isPending) return;
    summarizeEvtx.mutate(
      { id: caseId, sURL: attachment.sURL },
      {
        onSuccess: (response) => {
          setEvtxSummary(response.data);
        },
        onError: (summaryError) => {
          toast(
            summaryError instanceof Error
              ? summaryError.message
              : `Could not summarize ${attachment.sFileName}`,
            6000
          );
        },
      }
    );
  };

  const summarizingUrl = summarizeEvtx.isPending ? summarizeEvtx.variables.sURL : null;

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <div className="text-muted-foreground">Loading ticket...</div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <Card className="border-red-500/50 bg-red-500/5">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="font-medium">Failed to load ticket</p>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {error instanceof Error ? error.message : "Unknown error"}
            </p>
          </CardContent>
        </Card>
        <Button onClick={() => navigate("/fogbugz/tickets")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tickets
        </Button>
      </div>
    );
  }

  const isResolved =
    caseData.sStatus.toLowerCase().includes("resolved") ||
    caseData.sStatus.toLowerCase().includes("closed");

  const events = caseData.events ?? [];
  const allAttachments = events.flatMap(
    (event) => event.rgAttachments ?? []
  );

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Back to tickets"
          onClick={() => navigate("/fogbugz/tickets")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="text-2xl font-bold"
              />
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateCase.isPending || !editedTitle.trim()}
              >
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-muted-foreground">#{caseData.ixBug}</span>
              <h1 className="text-2xl font-bold">{caseData.sTitle}</h1>
              <Button variant="ghost" size="sm" aria-label="Edit title" onClick={handleEdit}>
                <Edit2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Badge variant="outline">{caseData.sStatus}</Badge>
        <Badge variant="outline">{caseData.sPriority}</Badge>
        {caseData.sProject && <Badge variant="outline">{caseData.sProject}</Badge>}
        {linkedItem && (
          <Link
            to={
              linkedItem.type === "epic-request"
                ? `/epic-requests/${linkedItem.id}`
                : `/epics/${linkedItem.id}`
            }
          >
            <Badge className="bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 cursor-pointer gap-1">
              <ExternalLink className="h-3 w-3" />
              {linkedItem.identifier}
            </Badge>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <CommandCopyBox
            label="Convert to Epic Request"
            command={buildBugToErBridgeCommand(caseData.ixBug, host)}
            helperText={getAgentPromptExplanation(host)}
            commandHelpId={HELP_IDS.fogbugz.convertToER.command}
            copyButtonHelpId={HELP_IDS.fogbugz.convertToER.copyButton}
          />
        </CardContent>
      </Card>

      <div className="flex gap-2">
        {isResolved ? (
          <Button onClick={handleReopen} disabled={reopenCase.isPending}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Reopen
          </Button>
        ) : (
          <Button onClick={handleResolve} disabled={resolveCase.isPending}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Resolve
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <span className="text-sm font-medium text-muted-foreground">Assigned To:</span>
            <p>{caseData.sPersonAssignedTo || "Unassigned"}</p>
          </div>
          {caseData.sArea && (
            <div>
              <span className="text-sm font-medium text-muted-foreground">Area:</span>
              <p>{caseData.sArea}</p>
            </div>
          )}
          {caseData.sFixFor && (
            <div>
              <span className="text-sm font-medium text-muted-foreground">Milestone:</span>
              <p>{caseData.sFixFor}</p>
            </div>
          )}
          {caseData.sCategory && (
            <div>
              <span className="text-sm font-medium text-muted-foreground">Category:</span>
              <p>{caseData.sCategory}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {allAttachments.length > 0 && (
        <AttachmentsCard
          attachments={allAttachments}
          onOpenImage={openImage}
          onSummarizeEvtx={handleSummarizeEvtx}
          summarizingUrl={summarizingUrl}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Comments & Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {events.length === 0 ? (
            <p className="text-muted-foreground text-sm">No activity yet.</p>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <EventItem
                  key={event.ixBugEvent}
                  event={event}
                  fogbugzBaseUrl={fogbugzBaseUrl ?? null}
                  onOpenImage={openImage}
                />
              ))}
            </div>
          )}

          <Separator />

          <div className="space-y-3">
            <CardDescription>Add a Comment</CardDescription>
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write your comment..."
              rows={4}
            />
            <Button
              onClick={handleAddComment}
              disabled={addComment.isPending || !commentText.trim()}
            >
              Add Comment
            </Button>
          </div>

          <Separator />

          <div className="space-y-3">
            <CardDescription>Attach a File</CardDescription>
            <div className="flex items-center gap-2">
              <Input type="file" onChange={handleFileChange} className="flex-1" />
              <Button onClick={handleAttachFile} disabled={attachFile.isPending || !selectedFile}>
                <Upload className="mr-2 h-4 w-4" />
                Upload
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <EvtxSummaryDialog
        open={evtxSummary !== null}
        onOpenChange={(open) => {
          if (!open) setEvtxSummary(null);
        }}
        result={evtxSummary}
      />

      <Lightbox
        open={!!lightbox}
        onOpenChange={(open) => !open && setLightbox(null)}
        src={lightbox?.src ?? ""}
        alt={lightbox?.alt ?? ""}
      />
    </div>
  );
}

interface EventItemProps {
  event: CaseEvent;
  fogbugzBaseUrl?: string | null;
  onOpenImage?: (src: string, alt: string) => void;
}

function EventItem({ event, fogbugzBaseUrl, onOpenImage }: EventItemProps) {
  const date = new Date(event.dt).toLocaleString();

  // Prefer sanitized sHtml rich content, fall back to sText/sChanges
  const sanitizedHtml = useMemo(() => {
    if (event.sHtml) {
      const result = sanitizeFogBugzHtml(event.sHtml, fogbugzBaseUrl);
      if (result) return result;
    }
    return null;
  }, [event.sHtml, fogbugzBaseUrl]);

  const plainText = sanitizedHtml ? null : (event.sText ?? event.sChanges ?? "");

  return (
    <div className="border-l-2 border-muted pl-4 py-2">
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium">{event.sPerson}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground text-xs">{date}</span>
      </div>
      {event.sVerb && <div className="text-xs text-muted-foreground mt-1">{event.sVerb}</div>}
      {sanitizedHtml && (
        <AuthenticatedHtmlContent
          html={sanitizedHtml}
          className="mt-2 text-sm prose prose-sm prose-invert max-w-none
            [&_a]:text-blue-400 [&_a]:underline"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (
              target.tagName === "IMG" &&
              target.dataset.fogbugzImage === "1"
            ) {
              e.preventDefault();
              const img = target as HTMLImageElement;
              if (img.src && img.src.startsWith("blob:")) {
                onOpenImage?.(img.src, img.alt || "");
              }
            }
          }}
          onKeyDown={(e) => {
            const target = e.target as HTMLElement;
            if (
              (e.key === "Enter" || e.key === " ") &&
              target.tagName === "IMG" &&
              target.dataset.fogbugzImage === "1"
            ) {
              e.preventDefault();
              const img = target as HTMLImageElement;
              if (img.src && img.src.startsWith("blob:")) {
                onOpenImage?.(img.src, img.alt || "");
              }
            }
          }}
        />
      )}
      {plainText && <div className="mt-2 text-sm whitespace-pre-wrap">{plainText}</div>}
    </div>
  );
}
